import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { DateUtils, useDebouncedEffect } from '@grandvisionhq/utils';
import { getClosedDates, getCalendarTimeSlotsAvailability, firstAvailableDate } from '../../../../components/AvailabilityCalendar/utils.js';
import { useNextAvailableDate } from '../../../../components/NoTimeSlots/hooks/use-next-available-date.js';
import { useNextWeekDate } from '../../../../components/NoTimeSlots/hooks/use-next-week-date.js';
import { useNoTimeSlotsVariant } from '../../../../components/NoTimeSlots/hooks/use-no-timeslot-variant.js';
import { Variant } from '../../../../components/NoTimeSlots/types.js';
import { useTimeFilterContext } from '../../../../context/TimeFilterContext/TimeFilterContext.js';
import { rescheduleAppointmentScreenEvent } from '../../../../data-layer/actions.js';
import { usePreferredTimeDataLayerEventDispatchers } from '../../../../data-layer/hooks/use-preferred-time-dl-event-dispatchers.js';
import useCalendarAvailability from '../../../../hooks/api/use-calendar-availability.js';
import useTimeSlots from '../../../../hooks/api/use-time-slots.js';
import '@grandvisionhq/graphql';
import '../../../../queries/book-appointment.js';
import '../../../../queries/get-calendar-availability.js';
import '../../../../queries/get-time-slots.js';
import '../../../../queries/get-store-offer-by-code.js';
import '../../../../queries/get-user.js';
import '../../../../queries/get-store-opening-times.js';
import '../../../../queries/get-availability-stores-nearby.js';
import '@grandvisionhq/stores-v2';
import { useStoreOpeningTimes } from '../../../../hooks/api/use-store-opening-times.js';
import { useActiveTimeFilters } from '../../../../hooks/use-active-time-filters.js';
import useVirtualAppointment from '../../../BookAppointment/hooks/use-virtual-appointment.js';
import { fromArrayToString } from '../../../../utils/from-array-to-string.js';
import { isPreferredTimeFilterActive } from '../../../../utils/is-preferred-time-filter-active.js';
import { useRescheduleAppointmentContext } from '../../context/context.js';
import { useRoutingContext } from '../../context/RoutingContext/context.js';
import useAppointmentById from '../../hooks/api/use-appointment-by-id.js';
import useAppointmentByToken from '../../hooks/api/use-appointment-by-token.js';
import useStoreByCode from '../../hooks/api/use-store-by-code.js';
import useRescheduleAppointmentById from '../../hooks/api/use-reschedule-appointment-by-id.js';
import useRescheduleAppointmentByToken from '../../hooks/api/use-reschedule-appointment-by-token.js';
import RescheduleAppointmentRoot from './RescheduleAppointment.js';

const INTERNAL_SERVER_ERROR_FAILURE = 'InternalServerErrorFailure';
const RescheduleAppointmentRootContainer = ({ cancelAppointmentUrl , userToken , appointmentId , token ,  })=>{
    const { getLabel  } = useIntl();
    const { featureConfigs  } = useFeatureToggles();
    const { appointment: currentAppointment , setAppointment: setCurrentAppointment , store , setStore , selectedTimeSlot , setSelectedTimeSlot ,  } = useRescheduleAppointmentContext();
    const [failure, setFailure] = React.useState('');
    const [, { active  }] = useTimeFilterContext();
    const [{ goTo  }, { queryParams  }] = useRoutingContext();
    const appointmentBookingAvailabiltyCalendarWeekCount = featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.appointmentBookingAvailabiltyCalendarWeekCount;
    const [getStoreOpeningTimes, storeOpeningTimes] = useStoreOpeningTimes();
    const [getCalendarAvailability, calendarAvailability] = useCalendarAvailability();
    const [{ getAvailabilityTimeFilters  }, { activeFilters: activeTimeFilters  }] = useActiveTimeFilters();
    const today = React.useRef(new Date());
    const [weekDate, setWeekDate] = React.useState();
    const [weeks1, setWeeks] = React.useState();
    const [date1, setDate] = React.useState();
    const [availabilityRequestParamsChanged, setAvailabilityRequestParamsChanged] = React.useState(true);
    const [showOtherTimeSlots, setShowOtherTimeSlots] = React.useState(false);
    const calendarLoading = storeOpeningTimes.loading || calendarAvailability.loading || availabilityRequestParamsChanged;
    const labels = React.useMemo(()=>({
            title: getLabel('appointments.page.reschedule.title'),
            currentAppointment: getLabel('appointments.page.reschedule.currentAppointment'),
            noAppointment: getLabel('appointments.page.reschedule.noAppointment'),
            selectADateAndTime: getLabel('appointments.page.reschedule.selectADateAndTime'),
            selectATime: getLabel('appointments.page.reschedule.selectATime'),
            selectedDateAndTime: getLabel('appointments.page.reschedule.selectedDateAndTime'),
            showOtherTimeSlots: getLabel('appointments.page.reschedule.showOtherTimeSlots'),
            hideOtherTimeSlots: getLabel('appointments.page.reschedule.hideOtherTimeSlots'),
            otherTimeSlots: getLabel('appointments.page.reschedule.otherTimeSlots'),
            rescheduleAppointment: getLabel('appointments.page.reschedule.rescheduleAppointment'),
            close: getLabel('appointments.page.reschedule.close'),
            cantFindSlot: getLabel('appointments.page.reschedule.cantFindSlot'),
            slowLoadingIndication: getLabel('appointments.page.reschedule.slowLoadingIndication'),
            cancelYourAppointment: getLabel('appointments.page.reschedule.cancelYourAppointment'),
            errorTitle: getLabel('appointments.page.reschedule.errorTitle'),
            errorMessage: getLabel(`appointments.page.reschedule.error.${failure}`, {
                hideLabelKey: true
            })
        })
    , [
        getLabel,
        failure
    ]);
    const closedDates = React.useMemo(()=>{
        if (!weeks1 || !storeOpeningTimes.data) {
            return [];
        }
        return getClosedDates(weeks1, storeOpeningTimes.data);
    }, [
        weeks1,
        storeOpeningTimes.data
    ]);
    const [getCurrentAppointmentById, currentAppointmentById] = useAppointmentById({
        id: appointmentId !== null && appointmentId !== void 0 ? appointmentId : ''
    }, userToken);
    const [getCurrentAppointmentByToken, currentAppointmentByToken] = useAppointmentByToken({
        token: token !== null && token !== void 0 ? token : ''
    });
    const [getTimeSlots, timeSlots] = useTimeSlots({
        timeFilters: activeTimeFilters
    });
    const [rescheduleAppointmentById, rescheduledAppointmentById] = useRescheduleAppointmentById(userToken !== null && userToken !== void 0 ? userToken : '');
    const [rescheduleAppointmentByToken, rescheduledAppointmentByToken] = useRescheduleAppointmentByToken();
    const [getStoreByCode, storeByCode] = useStoreByCode({
        code: 0
    });
    const nextAvailableDate = useNextAvailableDate(date1, calendarAvailability, Boolean(!timeSlots.called || calendarLoading));
    const nextWeekDate = useNextWeekDate(weeks1, !date1 || Boolean(nextAvailableDate));
    const noTimeSlotsVariant = useNoTimeSlotsVariant(timeSlots, nextAvailableDate, date1, closedDates, calendarLoading, isPreferredTimeFilterActive(active));
    const onCalendarWeeksChange = React.useCallback((weeks)=>{
        setAvailabilityRequestParamsChanged(true);
        setWeeks(weeks);
    }, []);
    const onSelectDate = React.useCallback((date)=>{
        setDate(date);
    }, []);
    const onSelectTime = React.useCallback((slot)=>{
        setSelectedTimeSlot(slot);
    }, [
        setSelectedTimeSlot
    ]);
    const noTimeSlotsAction = React.useCallback(()=>{
        switch(noTimeSlotsVariant){
            case Variant.time:
            case Variant.date:
            case Variant.closedDate:
                /* istanbul ignore if */ if (!nextAvailableDate) {
                    // This should never happen, but it's here to satisfy typescript
                    return;
                }
                setDate(nextAvailableDate);
                break;
            case Variant.week:
            case Variant.closedWeek:
                /* istanbul ignore if */ if (!nextWeekDate) {
                    // This should never happen, but it's here to satisfy typescript
                    return;
                }
                setWeekDate(nextWeekDate);
                setDate(nextWeekDate);
                break;
            case Variant.error:
                var ref, ref1;
                var ref2;
                const storeCode = (ref2 = currentAppointment === null || currentAppointment === void 0 ? void 0 : (ref = currentAppointment.store) === null || ref === void 0 ? void 0 : ref.code) !== null && ref2 !== void 0 ? ref2 : undefined;
                var ref3;
                const appointmentTypeId = (ref3 = currentAppointment === null || currentAppointment === void 0 ? void 0 : (ref1 = currentAppointment.appointmentType) === null || ref1 === void 0 ? void 0 : ref1.id) !== null && ref3 !== void 0 ? ref3 : undefined;
                /* istanbul ignore if */ if (!date1 || storeCode === undefined || appointmentTypeId === undefined) {
                    // This should never happen, but it's here to satisfy typescript
                    return;
                }
                getTimeSlots({
                    variables: {
                        storeCode,
                        appointmentTypeId,
                        date: DateUtils.toLocalDate(date1)
                    }
                });
            case Variant.none:
                return;
        }
    }, [
        noTimeSlotsVariant,
        nextAvailableDate,
        nextWeekDate,
        getTimeSlots,
        date1,
        currentAppointment
    ]);
    const hasNoTimeSlotsAvailableInCalendar = getCalendarTimeSlotsAvailability(calendarAvailability.data, noTimeSlotsVariant);
    React.useEffect(()=>{
        if (currentAppointment && store) {
            var ref, ref5;
            var ref6;
            const dataLayerEvent = rescheduleAppointmentScreenEvent({
                appointment: {
                    startTime: currentAppointment.startTime,
                    endTime: currentAppointment.endTime,
                    appointmentType: {
                        id: ((ref = currentAppointment.appointmentType) === null || ref === void 0 ? void 0 : ref.id) || '',
                        ageGroup: (ref6 = (ref5 = currentAppointment.appointmentType) === null || ref5 === void 0 ? void 0 : ref5.ageGroup) !== null && ref6 !== void 0 ? ref6 : 'ADULT'
                    }
                },
                store
            });
            DataLayer.pushEvent(dataLayerEvent);
        }
    }, [
        currentAppointment,
        store
    ]);
    React.useEffect(()=>{
        if (appointmentId) {
            getCurrentAppointmentById({
                variables: {
                    id: appointmentId
                }
            });
        }
    }, [
        appointmentId,
        getCurrentAppointmentById
    ]);
    React.useEffect(()=>{
        if (token) {
            getCurrentAppointmentByToken({
                variables: {
                    token
                }
            });
        }
    }, [
        token,
        getCurrentAppointmentByToken
    ]);
    React.useEffect(()=>{
        if (!currentAppointmentById.data) {
            return;
        }
        setCurrentAppointment(currentAppointmentById.data);
    }, [
        setCurrentAppointment,
        currentAppointmentById.data
    ]);
    React.useEffect(()=>{
        if (!currentAppointmentByToken.data) {
            return;
        }
        setCurrentAppointment(currentAppointmentByToken.data);
    }, [
        setCurrentAppointment,
        currentAppointmentByToken.data
    ]);
    React.useEffect(()=>{
        const INTERNAL_SERVER_ERROR_STATUS = 'INTERNAL_SERVER_ERROR';
        if (currentAppointmentById.error) {
            var ref, ref7, ref8;
            var ref9;
            throw new Error((ref9 = (ref = currentAppointmentById.error.graphQLErrors) === null || ref === void 0 ? void 0 : (ref7 = ref[0]) === null || ref7 === void 0 ? void 0 : (ref8 = ref7.extensions) === null || ref8 === void 0 ? void 0 : ref8.code) !== null && ref9 !== void 0 ? ref9 : INTERNAL_SERVER_ERROR_STATUS);
        }
        if (currentAppointmentByToken.error) {
            var ref10, ref11, ref12, ref13;
            var ref14;
            throw new Error((ref14 = (ref10 = currentAppointmentByToken.error) === null || ref10 === void 0 ? void 0 : (ref11 = ref10.graphQLErrors) === null || ref11 === void 0 ? void 0 : (ref12 = ref11[0]) === null || ref12 === void 0 ? void 0 : (ref13 = ref12.extensions) === null || ref13 === void 0 ? void 0 : ref13.code) !== null && ref14 !== void 0 ? ref14 : INTERNAL_SERVER_ERROR_STATUS);
        }
    }, [
        currentAppointmentById.error,
        currentAppointmentByToken.error
    ]);
    React.useEffect(()=>{
        var ref;
        const storeCode = currentAppointment === null || currentAppointment === void 0 ? void 0 : (ref = currentAppointment.store) === null || ref === void 0 ? void 0 : ref.code;
        if (storeCode) {
            getStoreByCode({
                variables: {
                    code: +storeCode
                }
            });
        }
    }, [
        getStoreByCode,
        currentAppointment
    ]);
    React.useEffect(()=>{
        if (!storeByCode.data) {
            return;
        }
        setStore(storeByCode.data);
    }, [
        setStore,
        storeByCode.data
    ]);
    React.useEffect(()=>{
        if (!store) {
            return;
        }
        getStoreOpeningTimes({
            variables: {
                code: store.code
            }
        });
    }, [
        getStoreOpeningTimes,
        store
    ]);
    React.useEffect(()=>setAvailabilityRequestParamsChanged(true)
    , [
        currentAppointment
    ]);
    useDebouncedEffect(()=>{
        var ref, ref15, ref16;
        const appointmentTypeId = currentAppointment === null || currentAppointment === void 0 ? void 0 : (ref = currentAppointment.appointmentType) === null || ref === void 0 ? void 0 : ref.id;
        const storeCode = currentAppointment === null || currentAppointment === void 0 ? void 0 : (ref15 = currentAppointment.store) === null || ref15 === void 0 ? void 0 : ref15.code;
        if (!appointmentTypeId || !storeCode || !weeks1) {
            /* istanbul ignore else */ if (process.env.NODE_ENV === 'test') {
                console.log('Awaiting availability request variables data');
            }
            return;
        }
        const firstWeekDay = (ref16 = weeks1[0]) === null || ref16 === void 0 ? void 0 : ref16[0];
        const lastWeek = weeks1[weeks1.length - 1];
        const lastWeekDay = lastWeek === null || lastWeek === void 0 ? void 0 : lastWeek[lastWeek.length - 1];
        if (firstWeekDay && lastWeekDay) {
            getCalendarAvailability(swcHelpers.objectSpread({
                appointmentTypeId,
                storeCode: storeCode.toString(),
                startDate: firstWeekDay,
                endDate: lastWeekDay
            }, getAvailabilityTimeFilters()));
        }
        setAvailabilityRequestParamsChanged(false);
    }, 400, [
        getCalendarAvailability,
        currentAppointment,
        weeks1,
        getAvailabilityTimeFilters
    ]);
    React.useEffect(()=>{
        const dateIsAlreadySelected = Boolean(date1);
        const waitingForAvailability = !calendarAvailability.called || calendarAvailability.loading;
        if (dateIsAlreadySelected || !weeks1 || waitingForAvailability) {
            return;
        }
        const availableDate = firstAvailableDate(weeks1, calendarAvailability.data);
        const fallbackDate = today.current;
        setDate(availableDate !== null && availableDate !== void 0 ? availableDate : fallbackDate);
    }, [
        date1,
        weeks1,
        calendarAvailability
    ]);
    React.useEffect(()=>{
        calendarAvailability.error && console.error('Error fetching appointment reschedule availabilty:', calendarAvailability.error.message);
    }, [
        calendarAvailability.error
    ]);
    React.useEffect(()=>{
        var ref, ref17;
        var ref18;
        const storeCode = (ref18 = currentAppointment === null || currentAppointment === void 0 ? void 0 : (ref = currentAppointment.store) === null || ref === void 0 ? void 0 : ref.code) !== null && ref18 !== void 0 ? ref18 : undefined;
        var ref19;
        const appointmentTypeId = (ref19 = currentAppointment === null || currentAppointment === void 0 ? void 0 : (ref17 = currentAppointment.appointmentType) === null || ref17 === void 0 ? void 0 : ref17.id) !== null && ref19 !== void 0 ? ref19 : undefined;
        if (!date1 || storeCode === undefined || appointmentTypeId === undefined) {
            return;
        }
        getTimeSlots({
            variables: {
                storeCode,
                appointmentTypeId,
                date: DateUtils.toLocalDate(date1)
            }
        });
    }, [
        currentAppointment,
        getTimeSlots,
        date1
    ]);
    React.useEffect(()=>setSelectedTimeSlot(undefined)
    , [
        setSelectedTimeSlot,
        date1
    ]);
    const onRescheduleButtonClick = React.useCallback(()=>{
        setFailure('');
        if (appointmentId && selectedTimeSlot) {
            rescheduleAppointmentById({
                variables: {
                    id: appointmentId,
                    startTime: DateUtils.toLocalDateTime(selectedTimeSlot === null || selectedTimeSlot === void 0 ? void 0 : selectedTimeSlot.from),
                    endTime: DateUtils.toLocalDateTime(selectedTimeSlot === null || selectedTimeSlot === void 0 ? void 0 : selectedTimeSlot.until)
                }
            });
        }
        if (token && selectedTimeSlot) {
            rescheduleAppointmentByToken({
                variables: {
                    token,
                    startTime: DateUtils.toLocalDateTime(selectedTimeSlot === null || selectedTimeSlot === void 0 ? void 0 : selectedTimeSlot.from),
                    endTime: DateUtils.toLocalDateTime(selectedTimeSlot === null || selectedTimeSlot === void 0 ? void 0 : selectedTimeSlot.until)
                }
            });
        }
    }, [
        appointmentId,
        token,
        selectedTimeSlot,
        rescheduleAppointmentById,
        rescheduleAppointmentByToken, 
    ]);
    const onToggleOtherTimeSlots = React.useCallback(()=>setShowOtherTimeSlots((curr)=>!curr
        )
    , []);
    React.useEffect(()=>{
        var ref;
        if (!((ref = rescheduledAppointmentById.data) === null || ref === void 0 ? void 0 : ref.appointment)) {
            return;
        }
        setCurrentAppointment(rescheduledAppointmentById.data.appointment);
        goTo('confirmation', queryParams);
    }, [
        rescheduledAppointmentById.data,
        queryParams,
        setCurrentAppointment,
        goTo
    ]);
    React.useEffect(()=>{
        var ref;
        if (!((ref = rescheduledAppointmentByToken.data) === null || ref === void 0 ? void 0 : ref.appointment)) {
            return;
        }
        setCurrentAppointment(rescheduledAppointmentByToken.data.appointment);
        goTo('confirmation', queryParams);
    }, [
        rescheduledAppointmentByToken.data,
        queryParams,
        setCurrentAppointment,
        goTo
    ]);
    React.useEffect(()=>{
        var ref, ref20, ref21;
        var _error;
        const error = (_error = rescheduledAppointmentById.error) !== null && _error !== void 0 ? _error : rescheduledAppointmentByToken.error;
        if (error) {
            setFailure(INTERNAL_SERVER_ERROR_FAILURE);
        }
        var ref22;
        const failures = (ref22 = (ref = rescheduledAppointmentById.data) === null || ref === void 0 ? void 0 : ref.failures) !== null && ref22 !== void 0 ? ref22 : (ref20 = rescheduledAppointmentByToken.data) === null || ref20 === void 0 ? void 0 : ref20.failures;
        if (failures && (failures === null || failures === void 0 ? void 0 : failures.length) > 0 && ((ref21 = failures[0]) === null || ref21 === void 0 ? void 0 : ref21.type)) {
            setFailure(failures[0].type);
        }
    }, [
        rescheduledAppointmentById,
        rescheduledAppointmentByToken
    ]);
    const buttonState = React.useMemo(()=>({
            disabled: !selectedTimeSlot,
            loading: rescheduledAppointmentById.loading || rescheduledAppointmentByToken.loading
        })
    , [
        selectedTimeSlot,
        rescheduledAppointmentById.loading,
        rescheduledAppointmentByToken.loading
    ]);
    const [{ isVirtualAppointmentType  }] = useVirtualAppointment();
    const { onPreferredTimeClick , onPreferredTimeFilterClick  } = usePreferredTimeDataLayerEventDispatchers({
        appointmentCondition: 'reschedule',
        isVirtualAppointment: isVirtualAppointmentType(currentAppointment === null || currentAppointment === void 0 ? void 0 : currentAppointment.appointmentType),
        type: currentAppointment === null || currentAppointment === void 0 ? void 0 : currentAppointment.appointmentType,
        store,
        timeSlot: selectedTimeSlot
    });
    var ref4;
    return /*#__PURE__*/ React.createElement(RescheduleAppointmentRoot, {
        labels: labels,
        returnPath: (ref4 = fromArrayToString(queryParams.returnPath)) !== null && ref4 !== void 0 ? ref4 : '/',
        cancelAppointmentRoute: cancelAppointmentUrl,
        appointment: currentAppointment !== null && currentAppointment !== void 0 ? currentAppointment : undefined,
        loading: currentAppointmentById.loading || currentAppointmentByToken.loading,
        calendarLoading: calendarLoading,
        calendarWeekDate: weekDate,
        calendarClosedDates: closedDates,
        calendarAvailability: calendarAvailability.data,
        calendarWeekCount: appointmentBookingAvailabiltyCalendarWeekCount,
        onCalendarWeeksChange: onCalendarWeeksChange,
        selectedDate: date1,
        onSelectDate: onSelectDate,
        timeSlotsLoading: !timeSlots.called || timeSlots.loading,
        timeSlots: timeSlots.filtered.included,
        selectedTimeSlot: selectedTimeSlot,
        onSelectTime: onSelectTime,
        noTimeSlotsVariant: noTimeSlotsVariant,
        noTimeSlotsAction: noTimeSlotsAction,
        nextAvailableDate: nextAvailableDate,
        noMoreAvailabilityUntilDate: nextWeekDate,
        onRescheduleButtonClick: onRescheduleButtonClick,
        hasError: !!failure,
        buttonState: buttonState,
        showOtherTimeSlots: showOtherTimeSlots,
        onToggleOtherTimeSlots: onToggleOtherTimeSlots,
        otherTimeSlots: timeSlots.filtered.excluded,
        onPreferredTimeClick: onPreferredTimeClick,
        onPreferredTimeFilterClick: onPreferredTimeFilterClick,
        hasNoTimeSlotsAvailableInCalendar: hasNoTimeSlotsAvailableInCalendar
    });
};

export { RescheduleAppointmentRootContainer as default };
//# sourceMappingURL=RescheduleAppointmentContainer.js.map
